import {
  faBook,
  faFileAlt,
  faKeynote,
  faLayerGroup,
  faNewspaper,
  faChalkboardTeacher,
  faMicrophoneStand,
  faUserHeadset,
  faUsersClass,
  faPresentation,
  faCamcorder,
  faFilm,
  faTv,
  faVideo,
  faBookReader,
  faTheaterMasks,
} from '@fortawesome/pro-solid-svg-icons';
import {
  faBook as falBook,
  faFileAlt as falFileAlt,
  faKeynote as falKeynote,
  faLayerGroup as falLayerGroup,
  faNewspaper as falNewspaper,
  faChalkboardTeacher as falChalkboardTeacher,
  faMicrophoneStand as falMicrophoneStand,
  faUserHeadset as falUserHeadset,
  faUsersClass as falUsersClass,
  faPresentation as falPresentation,
  faCamcorder as falCamcorder,
  faFilm as falFilm,
  faTv as falTv,
  faVideo as falVideo,
  faBookReader as falBookReader,
  faTheaterMasks as falTheaterMasks,
} from '@fortawesome/pro-light-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

const LANG_OPTIONS = {
  // `all` option isn't sent from server, it is added manually later
  all: 'All',
  ar: 'Arabic',
  bn: 'Bengali',
  da: 'Danish',
  de: 'German',
  el: 'Greek',
  en: 'English',
  es: 'Spanish',
  fr: 'French',
  hi: 'Hindi',
  hu: 'Hungarian',
  id: 'Indonesian',
  it: 'Italian',
  nl: 'Dutch',
  pa: 'Punjabi',
  pl: 'Polish',
  pt: 'Portuguese',
  ru: 'Russian',
  sv: 'Swedish',
  tr: 'Turkish',
  uk: 'Ukrainian',
  zh: 'Chinese',
};

export enum PubType {
  // `all` option isn't sent from server, it is added manually later
  All = 'all',
  JournalArticle = 'journal-article',
  ConferencePaper = 'conference-paper',
  Book = 'book',
  Other = 'other',
}

export enum VideoCategory {
  // `all` options isn't sent from the server, it is added manually later
  All = 'all',
  ConferencePresentation = 'Conference Presentation',
  ClassLecture = 'Class Lecture',
  Talk = 'Talk',
  Interview = 'Interview',
  Panel = 'Panel',
  Demo = 'Demo',
  Documentary = 'Documentary',
  FilmProject = 'Film Project',
  TvFeature = 'TV Feature',
  Reading = 'Reading',
  Performance = 'Performance',
  Other = 'Other',
}

export type LangCode = keyof typeof LANG_OPTIONS;

// Type of `stats` key on api response
export type Stats = {
  publicationTypeCounts: [PubType, number][];
  publicationYearCounts: [number, number][];
  languageCounts: [LangCode, number][];
  videoCategoryCounts: [VideoCategory, number][];
};

export type HashifiedStats = {
  // How works of each type
  publicationTypeCounts: { [key in PubType]: number };
  // How many works per year
  publicationYearCounts: { [key: number]: number };
  // How many works of each language
  languageCounts: { [key in LangCode]: number };
  // How many videos of each category
  videoCategoryCounts: { [key in VideoCategory]: number };
};

type Selected = {
  langs: LangCode[];
  pubType: [PubType];
  videoCategory: VideoCategory[];
};

export interface FilterOption<T> {
  name: string;
  value: T;
  count: number | null;
  icon?: IconDefinition;
  selectedIcon?: IconDefinition;
}

const PUB_TYPE_OPTIONS: {
  [key in PubType]: Omit<FilterOption<PubType>, 'value' | 'count'>;
} = {
  [PubType.All]: {
    icon: falLayerGroup,
    name: 'All',
    selectedIcon: faLayerGroup,
  },
  [PubType.JournalArticle]: {
    icon: falFileAlt,
    name: 'Journal Article',
    selectedIcon: faFileAlt,
  },
  [PubType.ConferencePaper]: {
    icon: falKeynote,
    name: 'Conference Paper',
    selectedIcon: faKeynote,
  },
  [PubType.Book]: {
    icon: falBook,
    name: 'Book',
    selectedIcon: faBook,
  },
  [PubType.Other]: {
    icon: falNewspaper,
    name: 'Other',
    selectedIcon: faNewspaper,
  },
};

const VIDEO_CATEGORY_OPTIONS = {
  [VideoCategory.All]: {
    icon: falLayerGroup,
    name: 'All',
    selectedIcon: faLayerGroup,
  },
  [VideoCategory.ConferencePresentation]: {
    icon: falKeynote,
    name: 'Conference Presentation',
    selectedIcon: faKeynote,
  },
  [VideoCategory.ClassLecture]: {
    icon: falChalkboardTeacher,
    name: 'Class Lecture',
    selectedIcon: faChalkboardTeacher,
  },
  [VideoCategory.Talk]: {
    icon: falMicrophoneStand,
    name: 'Talk',
    selectedIcon: faMicrophoneStand,
  },
  [VideoCategory.Interview]: {
    icon: falUserHeadset,
    name: 'Interview',
    selectedIcon: faUserHeadset,
  },
  [VideoCategory.Panel]: {
    icon: falUsersClass,
    name: 'Panel',
    selectedIcon: faUsersClass,
  },
  [VideoCategory.Demo]: {
    icon: falPresentation,
    name: 'Demo',
    selectedIcon: faPresentation,
  },
  [VideoCategory.Documentary]: {
    icon: falCamcorder,
    name: 'Documentary',
    selectedIcon: faCamcorder,
  },
  [VideoCategory.FilmProject]: {
    icon: falFilm,
    name: 'Film Project',
    selectedIcon: faFilm,
  },
  [VideoCategory.TvFeature]: {
    icon: falTv,
    name: 'TV Feature',
    selectedIcon: faTv,
  },
  [VideoCategory.Reading]: {
    icon: falBookReader,
    name: 'Reading',
    selectedIcon: faBookReader,
  },
  [VideoCategory.Performance]: {
    icon: falTheaterMasks,
    name: 'Performance',
    selectedIcon: faTheaterMasks,
  },
  [VideoCategory.Other]: {
    icon: falVideo,
    name: 'Other',
    selectedIcon: faVideo,
  },
};

export const isLangDefault = (langs: LangCode[] | undefined) =>
  langs === undefined || (langs.length === 1 && langs[0] === 'all');

export const isPubTypeDefault = (pubType: PubType[] | undefined) =>
  pubType === undefined || pubType[0] === PubType.All;

export const isVideoCategoryDefault = (
  videoCategory: VideoCategory[] | undefined,
) => videoCategory === undefined || videoCategory[0] === VideoCategory.All;

export const isYearsDefault = (
  years: [number | undefined, number | undefined],
): boolean => years[0] == null && years[1] == null;

export const redirectToMarketing = (
  query: string,
  count: number | null,
  trigger = 'new-lft-nv',
) => {
  window.location.href = `/upgrade?feature=search&q=${query}&trigger=${trigger}&ct=${count}`;
};
// If the selected filters match the default values
// Filters are a premium only feature,
// so if a nonpremium user selects a different value,
// we use the result from this function to show a gate/upsell
export const areFiltersDefault = ({
  langs,
  pubType,
  videoCategory,
  years,
}: {
  langs: LangCode[];
  pubType: PubType[];
  videoCategory: VideoCategory[];
  years: [number | undefined, number | undefined];
}) =>
  isLangDefault(langs) &&
  isPubTypeDefault(pubType) &&
  isVideoCategoryDefault(videoCategory) &&
  isYearsDefault(years);

export const langCodeToName = (code: LangCode) => LANG_OPTIONS[code];

const getEmptyLangs: () => FilterOption<LangCode>[] = () =>
  Object.entries(LANG_OPTIONS).map(([code, name]) => ({
    name,
    value: code as LangCode,
    count: null,
  }));

const getEmptyPubTypes: () => FilterOption<PubType>[] = () =>
  Object.entries(PUB_TYPE_OPTIONS).map(([type, obj]) => ({
    ...obj,
    value: type as PubType,
    count: null,
  }));

const getEmptyVideoCategories: () => FilterOption<VideoCategory>[] = () =>
  Object.entries(VIDEO_CATEGORY_OPTIONS).map(([type, obj]) => ({
    ...obj,
    value: type as VideoCategory,
    count: null,
  }));

// Takes the stats returned from the server,
// turns them into usable option objects for the Filter components
type FilterOptions = {
  langs: FilterOption<LangCode>[];
  pubTypes: FilterOption<PubType>[];
  videoCategories: FilterOption<VideoCategory>[];
};
export const getFilterOptions = (
  stats: HashifiedStats | null,
  selected: Selected,
): FilterOptions => {
  if (stats === null || Object.keys(stats).length === 0) {
    return {
      langs: getEmptyLangs(),
      pubTypes: getEmptyPubTypes(),
      videoCategories: getEmptyVideoCategories(),
    };
  }

  const options: FilterOptions = {
    langs: Object.entries(stats.languageCounts)
      .filter(([lang]) => lang in LANG_OPTIONS)
      .map(([lang, count]) => ({
        count,
        name: LANG_OPTIONS[lang as LangCode],
        value: lang as LangCode,
      })),
    pubTypes: Object.entries(stats.publicationTypeCounts)
      .filter(([type]) => type in PUB_TYPE_OPTIONS)
      .map(([type, count]) => ({
        ...PUB_TYPE_OPTIONS[type as PubType],
        count,
        value: type as PubType,
      })),
    videoCategories: Object.entries(stats.videoCategoryCounts)
      .filter(([type]) => type in VIDEO_CATEGORY_OPTIONS)
      .map(([type, count]) => ({
        ...VIDEO_CATEGORY_OPTIONS[type as VideoCategory],
        count,
        value: type as VideoCategory,
      })),
  };

  const allCount =
    // `publicationTypeCounts` usually has fewest sections
    Object.keys(stats.publicationTypeCounts).length > 0
      ? Object.values(stats.publicationTypeCounts).reduce(
          (acc, count) => acc + count,
          0,
        )
      : // Videos do not have publication types, use `videoCategoryCounts` instead
        Object.values(stats.videoCategoryCounts).reduce(
          (acc, count) => acc + count,
          0,
        );

  // Only show the All count if we have the default selected
  const langsAllCount = isLangDefault(selected.langs) ? allCount : null;
  options.langs.unshift({ count: langsAllCount, name: 'All', value: 'all' });

  // Only show the All count if we have the default selected
  const pubTypesAllCount = isPubTypeDefault(selected.pubType) ? allCount : null;
  options.pubTypes.unshift({
    ...PUB_TYPE_OPTIONS[PubType.All],
    count: pubTypesAllCount,
    value: PubType.All,
  });

  // Only show the All count if we have the default selected
  const videoCategoriesAllCount = isVideoCategoryDefault(selected.videoCategory)
    ? allCount
    : null;
  options.videoCategories.unshift({
    ...VIDEO_CATEGORY_OPTIONS[VideoCategory.All],
    count: videoCategoriesAllCount,
    value: VideoCategory.All,
  });

  return options;
};

const formatter = new Intl.NumberFormat();
export const format = (num: number) => formatter.format(num);
